import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { toast } from 'react-toastify'
import { removeLoadingKey } from '../helpers/common'

export const ProductLoadingKeys = {
    getProducts: "GET_PRODUCTS",
    getDuplicatesBySkuAndBarcode: "GET_DUPLICATES_BY_SKU_AND_BARCODE",
    getDuplicatesBarcode: "GET_DUPLICATES_BY_BARCODE",
    generateBarcode: "GENERATE_BARCODE",
    updateBOMVariant: "UPDATE_BOM_VARIANT",
    updateVirtual: "UPDATE_VIRTUAL",
    updateOverriddenBySourceSku: "UPDATE_OVERRIDDEN_BY_SOURCE_SKU",
    updateBOMVariants: "UPDATE_BOM_VARIANTS",
    updateStatus: "UPDATE_STATUS",
    updateVendor: "UPDATE_VENDOR",
    updateAmazon: "UPDATE_AMAZON",
    updateKit: "UPDATE_KIT",
    updateReplanishable: "UPDATE_REPLENISHABLE",
    updateBarcode: "UPDATE_BARCODE",
    updateLeadTime: "UPDATE_LEAD_TIME",
    updateInactiveSku: "UPDATE_INACTIVE_SKU",
    updateSku: "UPDATE_SKU",
    updateName: "UPDATE_NAME",
    updateSourceSku: "UPDATE_SOURCE_SKU"
}

const initialState = {
  duplicatesBarcodeAndSku: null,
  duplicatesBarcode: null,
  products: [],
  singleProduct: {},
  isFirstLoadComplete: false,
  loadingKeys: [],
  pagination: {
    isTheLastPage: false,
    rangeRows: "",
    totalPages: 0
  },
  loading: false,
}

// duplicates (KL-26)
export const getDuplicatesBySkuAndBarcodeRequest = createAsyncThunk(
    'products/getDuplicatesBySkuAndBarcode',
    async ({ activeOnly }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`/api/v1/products/duplicates`, { params: {
              activeOnly
            }});
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const getDuplicatesByBarcodeRequest = createAsyncThunk(
    'products/getDuplicatesByBarcode',
    async ({ activeOnly }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`/api/v1/products/duplicate-barcodes`, { params: {
              activeOnly
            }});
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const getProductsRequest = createAsyncThunk(
  'products/getProductsRequest',
  async ({
    search,
    page, 
    phasedStatuses,
    amazon,
    kit,
    itemsPerPage,
    sortBy,
    sortDirection
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/get-products`, { params: { 
        search,
        phasedStatuses,
        amazon,
        kit,
        currentPage: page, 
        currentItemsPerPage: itemsPerPage,
        sortBy,
        sortDirection 
      }});
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const updateReplenishableByProduct = createAsyncThunk(
  'products/updateReplenishableByProduct',
  async ({ 
    productId, 
    isReplenishable,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-replenishable-by-product`, {
        productId,
        isReplenishable
      });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateVirtualByProduct = createAsyncThunk(
  'products/updateVirtualByProduct',
  async ({ 
    productId, 
    isVirtual,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-virtual-by-product`, {
        productId,
        isVirtual
      });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateOverriddenBySourceSkuByProduct = createAsyncThunk(
  'products/updateOverriddenBySourceSkuByProduct',
  async ({ 
    productId, 
    isOverriddenBySourceSkus,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-overridden-by-source-sku-by-product`, {
        productId,
        isOverriddenBySourceSkus
      });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateKitByProduct = createAsyncThunk(
  'products/updateKitByProduct',
  async ({ 
    productId, 
    isKitManually,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-kit-by-product`, {
        productId,
        isKitManually
      });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateAmazonByProduct = createAsyncThunk(
  'products/updateAmazonByProduct',
  async ({ 
    productId, 
    isAmazonManually,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-amazon-by-product`, {
        productId,
        isAmazonManually
      });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateInactiveSkuByProduct = createAsyncThunk(
  'products/updateInactiveSkuByProduct',
  async ({ 
    productId, 
    inactiveSku,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-inactive-sku-by-product`, {
        productId,
        inactiveSku
      });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const printProductBarcode = createAsyncThunk(
  'products/printProductBarcode',
  async ({ productData }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/generate-barcode`, productData);
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, productData })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, productData })
      }
    }
  }
)

export const updateLeadTimeByProduct = createAsyncThunk(
  'products/updateLeadTimeByProduct',
  async ({ 
    productId, 
    leadTime,
    search,
    page, 
    phasedStatuses,
    amazon,
    kit,
    itemsPerPage,
    sortBy,
    sortDirection
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-lead-time`, { productId, leadTime });
      await dispatch(getProductsRequest({
        search,
        page, 
        phasedStatuses,
        amazon,
        kit,
        itemsPerPage,
        sortBy,
        sortDirection
      }))
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateSkuByProduct = createAsyncThunk(
  'products/updateSkuByProduct',
  async ({ 
    productId, 
    sku,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-sku`, { productId, sku });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateNameByProduct = createAsyncThunk(
  'products/updateNameByProduct',
  async ({ 
    productId, 
    name,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-name`, { productId, name });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateBarcodeByProduct = createAsyncThunk(
  'products/updateBarcodeByProduct',
  async ({ 
    productId, 
    barcode,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-barcode`, { productId, barcode });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateVendorByProduct = createAsyncThunk(
  'products/updateVendorByProduct',
  async ({ 
    productId, 
    newVendor,
    requestToUpdate,
    skipSuccessToast,
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-vendor-by-variant`, { productId, newVendor });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateStatusByProduct = createAsyncThunk(
  'products/updateStatusByProduct',
  async ({ 
    productId, 
    newStatus,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-phased-status`, { productId, newPhasedStatus: newStatus });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateBOMVariantsByProduct = createAsyncThunk(
  'products/updateBOMVariantsByProduct',
  async ({ 
    productId, 
    bomProductSkus,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-bom-variants`, { productId, bomProductSkus });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateBOMVariantByProduct = createAsyncThunk(
  'products/updateBOMVariantByProduct',
  async ({ 
    productId, 
    bomProductSku,
    type,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-bom-variant`, { productId, bomProductSku, type });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateSourceSkuByProduct = createAsyncThunk(
  'products/updateSourceSkuByProduct',
  async ({ 
    productId, 
    sourceSku,
    type,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-source-sku`, { productId, sourceSku, type });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ 
          productId 
        }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateVendorByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateVendor];
      })
      .addCase(updateVendorByProduct.fulfilled, (state, action) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateVendor, state.loadingKeys);
          if (!action.meta.arg?.skipSuccessToast) {
            toast.success(
              "The vendor is updated.", 
              { position: "bottom-right" }
            );
          }
      })
      .addCase(updateVendorByProduct.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateVendor, state.loadingKeys);
          toast.error(
            `The vendor can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
      })
      .addCase(updateStatusByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateStatus];
      })
      .addCase(updateStatusByProduct.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateStatus, state.loadingKeys);
          toast.success(
            "The status is updated.", 
            { position: "bottom-right" }
          );
      })
      .addCase(updateStatusByProduct.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateStatus, state.loadingKeys);
          toast.error(
            `The status can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
      })
      .addCase(getDuplicatesBySkuAndBarcodeRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.getDuplicatesBySkuAndBarcode];
      })
      .addCase(getDuplicatesBySkuAndBarcodeRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.duplicatesBarcodeAndSku = action.payload?.data;
        state.loadingKeys = removeLoadingKey(ProductLoadingKeys.getDuplicatesBySkuAndBarcode, state.loadingKeys);
      })
      .addCase(getDuplicatesBySkuAndBarcodeRequest.rejected, (state) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(ProductLoadingKeys.getDuplicatesBySkuAndBarcode, state.loadingKeys);
      })
      .addCase(getDuplicatesByBarcodeRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.getDuplicatesBarcode];
      })
      .addCase(getDuplicatesByBarcodeRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.duplicatesBarcode = action.payload?.data;
        state.loadingKeys = removeLoadingKey(ProductLoadingKeys.getDuplicatesBarcode, state.loadingKeys);
      })
      .addCase(getDuplicatesByBarcodeRequest.rejected, (state) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(ProductLoadingKeys.getDuplicatesBarcode, state.loadingKeys);
      })
      .addCase(getProductsRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.getProducts];
      })
      .addCase(getProductsRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload?.items;
        state.isFirstLoadComplete = true;
        state.pagination.isTheLastPage = action.payload?.isTheLastPage;
        state.pagination.rangeRows = action.payload?.rangeRows;
        state.pagination.totalPages = action.payload?.totalPages;
        state.loadingKeys = removeLoadingKey(ProductLoadingKeys.getProducts, state.loadingKeys);
      })
      .addCase(getProductsRequest.rejected, (state) => {
        state.loading = false;
        state.isFirstLoadComplete = true;
        state.pagination.isTheLastPage = false;
        state.pagination.rangeRows = "";
        state.pagination.totalPages = 0;
        state.loadingKeys = removeLoadingKey(ProductLoadingKeys.getProducts, state.loadingKeys);
      })
      .addCase(updateReplenishableByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateReplanishable];
      })
      .addCase(updateReplenishableByProduct.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateReplanishable, state.loadingKeys);
          toast.success(
            "The replanishable is updated.", 
            { position: "bottom-right" }
          );
      })
      .addCase(updateReplenishableByProduct.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateReplanishable, state.loadingKeys);
          toast.error(
            `The replanishable can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
      })
      .addCase(updateVirtualByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateVirtual];
      })
      .addCase(updateVirtualByProduct.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateVirtual, state.loadingKeys);
          toast.success(
            "The virtual is updated.", 
            { position: "bottom-right" }
          );
      })
      .addCase(updateVirtualByProduct.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateVirtual, state.loadingKeys);
          toast.error(
            `The virtual can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
      })
      .addCase(updateOverriddenBySourceSkuByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateOverriddenBySourceSku];
      })
      .addCase(updateOverriddenBySourceSkuByProduct.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateOverriddenBySourceSku, state.loadingKeys);
          toast.success(
            "The overridden by source SKU is updated.", 
            { position: "bottom-right" }
          );
      })
      .addCase(updateOverriddenBySourceSkuByProduct.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateOverriddenBySourceSku, state.loadingKeys);
          toast.error(
            `The overridden by source SKU can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
      })
      .addCase(updateKitByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateKit];
      })
      .addCase(updateKitByProduct.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateKit, state.loadingKeys);
          toast.success(
            "The kit is updated.", 
            { position: "bottom-right" }
          );
      })
      .addCase(updateKitByProduct.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateKit, state.loadingKeys);
          toast.error(
            `The kit can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
      })
      .addCase(updateAmazonByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateAmazon];
      })
      .addCase(updateAmazonByProduct.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateAmazon, state.loadingKeys);
          toast.success(
            "The amazon is updated.", 
            { position: "bottom-right" }
          );
      })
      .addCase(updateAmazonByProduct.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateAmazon, state.loadingKeys);
          toast.error(
            `The amazon can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
      })
      .addCase(updateInactiveSkuByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateInactiveSku];
      })
      .addCase(updateInactiveSkuByProduct.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateInactiveSku, state.loadingKeys);
          toast.success(
            "The inactive SKU's is updated.", 
            { position: "bottom-right" }
          );
      })
      .addCase(updateInactiveSkuByProduct.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateInactiveSku, state.loadingKeys);
          toast.error(
            `The inactive SKU's can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
          ) 
      })
      .addCase(updateSkuByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateSku];
      })
      .addCase(updateSkuByProduct.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateSku, state.loadingKeys);
      })
      .addCase(updateSkuByProduct.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateSku, state.loadingKeys);
      })
      .addCase(updateNameByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateName];
      })
      .addCase(updateNameByProduct.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateName, state.loadingKeys);
      })
      .addCase(updateNameByProduct.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateName, state.loadingKeys);
      })
      .addCase(updateLeadTimeByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateLeadTime];
      })
      .addCase(updateLeadTimeByProduct.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateLeadTime, state.loadingKeys);
          toast.success(
            "Changes saved.", 
            { position: "bottom-right", autoClose: 3000 }
          );
      })
      .addCase(updateLeadTimeByProduct.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateLeadTime, state.loadingKeys);
          toast.error(
            `The lead time can't be accepted. Error - ${action.error?.message}.`, { position: "bottom-right", autoClose: 3000 }
          )
      })
      .addCase(updateBarcodeByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateBarcode];
      })
      .addCase(updateBarcodeByProduct.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateBarcode, state.loadingKeys);
      })
      .addCase(updateBarcodeByProduct.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateBarcode, state.loadingKeys);
      })
      .addCase(printProductBarcode.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.generateBarcode];
      })
      .addCase(printProductBarcode.fulfilled, (state) => {
          state.loading = false;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.generateBarcode, state.loadingKeys);
          toast.success(
            "Barcode generated.", 
            { position: "bottom-right" }
          );
      })
      .addCase(printProductBarcode.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductLoadingKeys.generateBarcode, state.loadingKeys);
          toast.error(
            `The barcode can't be generated. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
      })
      .addCase(updateBOMVariantsByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateBOMVariants];
      })
      .addCase(updateBOMVariantsByProduct.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateBOMVariants, state.loadingKeys);
        toast.success(
          "BOM variants updated successfully.", 
          { position: "bottom-right" }
        );
      })
      .addCase(updateBOMVariantsByProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message;
        state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateBOMVariants, state.loadingKeys);
        toast.error(
          `Failed to update BOM variants. Error - ${action.error?.message}.`, 
          { position: "bottom-right" }
        );
      })
      .addCase(updateBOMVariantByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateBOMVariant];
      })
      .addCase(updateBOMVariantByProduct.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateBOMVariant, state.loadingKeys);
        toast.success(
          "BOM variant updated successfully.", 
          { position: "bottom-right" }
        );
      })
      .addCase(updateBOMVariantByProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message;
        state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateBOMVariant, state.loadingKeys);
        toast.error(
          `Failed to update BOM variant. Error - ${action.error?.message}.`, 
          { position: "bottom-right" }
        );
      })
      .addCase(updateSourceSkuByProduct.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductLoadingKeys.updateSourceSku];
      })
      .addCase(updateSourceSkuByProduct.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateSourceSku, state.loadingKeys);
        toast.success(
          "Source SKU updated successfully.", 
          { position: "bottom-right" }
        );
      })
      .addCase(updateSourceSkuByProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message;
        state.loadingKeys = removeLoadingKey(ProductLoadingKeys.updateSourceSku, state.loadingKeys);
        toast.error(
          `Failed to update source SKU. Error - ${action.error?.message}.`, 
          { position: "bottom-right" }
        );
      })
  }
})

export const {} = productsSlice.actions;

export default productsSlice.reducer
